import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { Sparkles, Send, Loader2, Lightbulb } from "lucide-react";

const SUGGESTIONS = [
  "Create a campaign for a vegetarian restaurant in Frisco.",
  "Target competitor restaurant visitors within 5 miles.",
  "Recommend audiences for a Bollywood movie release.",
  "Create a campaign to drive store visits.",
  "Estimate budget needed to maintain a lead in a city council race.",
];

function renderMarkdown(text) {
  // Minimal markdown-ish renderer (headings, lists, bold, code) for chat
  const lines = String(text || "").split("\n");
  const out = [];
  let listType = null;
  lines.forEach((raw, i) => {
    const line = raw.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/`(.+?)`/g, '<code class="text-cyan-300 bg-white/5 px-1 rounded">$1</code>');
    if (/^### /.test(raw)) {
      if (listType) { out.push(`</${listType}>`); listType = null; }
      out.push(`<h4 class="font-display font-semibold text-white mt-3 mb-1">${line.replace(/^### /, '')}</h4>`);
    } else if (/^## /.test(raw)) {
      if (listType) { out.push(`</${listType}>`); listType = null; }
      out.push(`<h3 class="font-display font-bold text-white mt-4 mb-2 text-lg">${line.replace(/^## /, '')}</h3>`);
    } else if (/^# /.test(raw)) {
      if (listType) { out.push(`</${listType}>`); listType = null; }
      out.push(`<h2 class="font-display font-bold text-white mt-4 mb-2 text-xl">${line.replace(/^# /, '')}</h2>`);
    } else if (/^\s*[-*]\s/.test(raw)) {
      if (listType !== "ul") { if (listType) out.push(`</${listType}>`); out.push('<ul class="list-disc pl-5 space-y-1 my-2">'); listType = "ul"; }
      out.push(`<li>${line.replace(/^\s*[-*]\s/, '')}</li>`);
    } else if (/^\s*\d+\.\s/.test(raw)) {
      if (listType !== "ol") { if (listType) out.push(`</${listType}>`); out.push('<ol class="list-decimal pl-5 space-y-1 my-2">'); listType = "ol"; }
      out.push(`<li>${line.replace(/^\s*\d+\.\s/, '')}</li>`);
    } else if (raw.trim() === "") {
      if (listType) { out.push(`</${listType}>`); listType = null; }
      out.push("<br/>");
    } else {
      if (listType) { out.push(`</${listType}>`); listType = null; }
      out.push(`<p class="my-1">${line}</p>`);
    }
  });
  if (listType) out.push(`</${listType}>`);
  return out.join("");
}

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "## Hi, I'm the TotalReach AI Strategist 👋\n\nAsk me to design a campaign, recommend audiences, draft creative concepts, or estimate a budget. Use the suggestions on the right to get started.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || loading) return;
    setMessages(m => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: `⚠️ Error: ${e?.response?.data?.detail || e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Powered by Claude Sonnet 4.5"
        title="AI Campaign Assistant"
        subtitle="Plain-language strategy, audience recommendations, channel mix, and creative concepts."
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-3 flex flex-col" padded={false}>
          <div className="flex-1 overflow-y-auto p-6 space-y-4 max-h-[600px]" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-slate-900" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-lg px-4 py-3 ${m.role === "user" ? "bg-cyan-400/20 border border-cyan-400/30 text-white" : "bg-white/5 border border-white/10 text-slate-200"}`}>
                  <div className="prose prose-invert prose-sm text-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-slate-900" />
                </div>
                <div className="rounded-lg px-4 py-3 bg-white/5 border border-white/10 text-slate-400 text-sm flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Strategizing…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="border-t border-white/10 p-4">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2" data-testid="chat-form">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything about your campaigns…"
                data-testid="chat-input"
                className="flex-1 bg-slate-900/60 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white focus:border-cyan-400/60 focus:outline-none"
              />
              <Btn type="submit" disabled={loading} data-testid="chat-send">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
              </Btn>
            </form>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">
            <Lightbulb className="w-3.5 h-3.5 text-cyan-300" /> Quick prompts
          </div>
          <div className="space-y-2">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} onClick={() => send(s)} data-testid={`prompt-${i}`}
                className="w-full text-left text-xs text-slate-300 hover:text-cyan-300 px-3 py-2 rounded-md border border-white/10 hover:border-cyan-400/30 hover:bg-white/5 transition-colors leading-snug">
                {s}
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
